<script setup>
// Vue twin of <x-radio-group> (resources/views/components/radio-group.blade.php +
// App\View\Components\RadioGroup). Emits the same markup and class hooks and
// carries no styling of its own: components/radio-options.scss styles both.
//
// Slot mode, like the Blade component: the caller writes the options
// (<RadioBox> or <RadioRich>), this arranges them and names the group.
//
// Two wrappers, one shape. A titled group is a <fieldset> so its legend is
// announced on entry and takes the group's required asterisk (the .field rule in
// form.scss); an untitled one is a plain <div>, because a fieldset with no legend
// groups nothing.
//
// The :style binding is a deliberate, narrow exception to the no-inline-styles
// rule, and the same call as ModeToggle's sliding indicator (fifth port): what it
// carries is a column count computed from a prop, not a design decision. Every
// visual property of the grid stays in the stylesheet - these two custom
// properties exist precisely so the CSS needs no math. The Blade component does
// the identical thing via ->style().
import { computed } from 'vue';
import SvgIcon from './SvgIcon.vue';

const props = defineProps({
    legend: {
        type: String,
        default: null,
    },
    // Passed through to SvgIcon, which owns icon validation - an unknown name
    // logs there and renders nothing.
    icon: {
        type: String,
        default: null,
    },
    // The most columns the grid may use, not a fixed count: the group gets that
    // many only when its own width allows, capped at two below that. Measured on
    // the group rather than the viewport, because these render inside pop-out
    // panels and form cards where the two have little to do with each other.
    maxAcross: {
        type: Number,
        default: 1,
    },
});

// Call-site shapes, so checked once at setup like the Blade constructor - none of
// these is runtime state. Both cases throw in Blade and log here: a throw in
// setup() takes down the whole app root rather than one group.
if (props.maxAcross < 1) {
    console.error(
        `[RadioGroup] max-across must be at least 1, got [${props.maxAcross}]. Using 1.`
    );
}

if (props.icon !== null && props.legend === null) {
    console.error(
        '[RadioGroup] icon requires a legend to sit on. Ignoring it.'
    );
}

const columns = computed(() => Math.max(1, props.maxAcross));

const styleVars = computed(() => ({
    '--radio-max-across': columns.value,
    '--radio-max-across-narrow': Math.min(2, columns.value),
}));
</script>

<template>
    <fieldset v-if="legend" class="radio_group field" :style="styleVars">
        <legend class="h4 icon_text"><SvgIcon v-if="icon" :name="icon" />{{ legend }}</legend>
        <div class="radio_group_options">
            <slot />
        </div>
    </fieldset>

    <div v-else class="radio_group" :style="styleVars">
        <div class="radio_group_options">
            <slot />
        </div>
    </div>
</template>
